// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { EducBox, ChartBox, ResultCard, InputRow, EDUC, baseChartOpts, showToastOnce, fmt } from './shared';

const ShareBtn = ({ text, testId }) => {
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ text, url: 'https://finsapiens.it' }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(text + '\nhttps://finsapiens.it'); toast.success('Copiato negli appunti ✓'); } catch { toast.error('Errore copia'); }
    }
  };
  return (
    <button onClick={share} data-testid={testId || 'share-btn'} className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg transition-colors hover:bg-white/[0.04]" style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C' }}>
      <Share2 className="w-3 h-3" />Condividi il risultato
    </button>
  );
};

const PACSimpleCalc = ({ initialMonthly, onStepComplete }) => {
  const navigate = useNavigate();
  const [monthly, setMonthly] = useState(initialMonthly || '300');
  const [rate, setRate] = useState('7');
  const [anni, setAnni] = useState('20');

  const m = parseFloat(monthly) || 0;
  const r = parseFloat(rate) || 0;
  const n = parseInt(anni) || 0;
  const rM = r / 100 / 12;
  const invested = m * 12 * n;
  const final = rM > 0 ? m * ((Math.pow(1 + rM, n * 12) - 1) / rM) : invested;
  const guadagno = final - invested;
  const shareText = `Investendo €${Math.round(m)}/mese per ${n} anni potrei avere €${fmt(final)}. Calcolalo anche tu 📈`;

  useEffect(() => {
    if (final > invested && final > 0) {
      showToastOnce('pac', () => {
        toast('Prossimo passo', {
          description: 'Vuoi sapere quando raggiungi la libertà finanziaria?',
          action: { label: 'Calcola il tuo FIRE', onClick: () => navigate('/calcolatori/simulatore-fire') },
          duration: 5000,
        });
      });
      if (onStepComplete) onStepComplete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [final > invested]);

  const labels = Array.from({ length: n + 1 }, (_, i) => i % 2 === 0 ? `Anno ${i}` : '');
  const dataLine = Array.from({ length: n + 1 }, (_, i) => {
    const ni = i * 12;
    return Math.round(rM > 0 ? m * ((Math.pow(1 + rM, ni) - 1) / rM) : m * ni);
  });
  const dataInvested = Array.from({ length: n + 1 }, (_, i) => Math.round(m * i * 12));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={EDUC.pac} />
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <InputRow label="Versamento mensile (€)" value={monthly} onChange={setMonthly} prefix="€" />
        <InputRow label="Rendimento annuo (%)" value={rate} onChange={setRate} suffix="%" />
        <InputRow label="Durata (anni)" value={anni} onChange={setAnni} suffix="anni" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <ResultCard label="Capitale finale" value={`€${fmt(final)}`} green />
        <ResultCard label="Versato" value={`€${fmt(invested)}`} />
        <ResultCard label="Guadagno netto" value={`€${fmt(guadagno)}`} gold />
      </div>
      {final > invested && <div className="mt-3 mb-1 flex justify-end"><ShareBtn text={shareText} testId="pac-share-btn" /></div>}
      <ChartBox title="Crescita del capitale nel tempo">
        <Line
          data={{
            labels,
            datasets: [
              { label: 'Capitale (con interessi)', data: dataLine, borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.15)', fill: true, tension: 0.4, pointRadius: 0 },
              { label: 'Versato (capitale)', data: dataInvested, borderColor: '#888780', backgroundColor: 'rgba(136,135,128,0.10)', borderDash: [4, 4], fill: true, pointRadius: 0 },
            ],
          }}
          options={{ ...baseChartOpts(), plugins: { ...baseChartOpts().plugins, legend: { display: true, labels: { color: '#888780' } } } }}
        />
      </ChartBox>
      <p className="mt-3 p-3 rounded-lg text-xs text-[#888780]" data-testid="pac-tax-note"
        style={{ background: 'rgba(61,122,82,0.06)', border: '1px solid rgba(61,122,82,0.2)' }}>
        <strong className="text-[#3D7A52]">Tassazione.</strong> La tassazione del 26% si applica alle plusvalenze al momento della vendita delle quote — non annualmente. Gli ETF ad accumulo non distribuiscono dividendi tassabili ogni anno.
      </p>
</motion.div>
  );
};

export default PACSimpleCalc;
